import React, { useState } from 'react';
import { get_api_url } from '../../../../utils/urls';
import { post_api } from '../../../../redux/api_funcs';
import * as actions from '../../../../redux/action_creators';
import { useDispatch } from 'react-redux';
import { CardIcon } from '../../../../utils/svg';

interface CreditTransferPayload {
  from_student_uuid: string;
  credits_to_transfer: number;
  to_student_uuid: string;
  reason: string;
}

interface TransferCreditsProps {
  onClose: () => void;
  hours_remaining: number;
  loadStudentData: () => void;
}

const TransferCredits = ({ onClose, hours_remaining, loadStudentData }: TransferCreditsProps) => {
  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const initialUUID = params?.get('id') || '';

  // PROD-9045: the transferable maximum is the cycle's remaining hours — the same number the
  // server enforces. Summing free_sessions + rollover_hours + total_paid_sessions read 0 for
  // prepaid members from cycle 2 on, whose balance lives in the upfront pool.
  const maxCredits = Number.isFinite(hours_remaining) ? hours_remaining : 0;

  const [targetStudentUUID, setTargetStudentUUID] = useState<string>('');
  const [transferCredits, setTransferCredits] = useState<number>(0);
  const [reasonForTransfer, setReasonForTransfer] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  const dispatch = useDispatch();
  const set_notification_variable = (show: boolean, type: 'success' | 'error', mes: string) => {
    dispatch(actions.set_notification_variable(show, type, mes));
  };

  const fail = (msg: string) => {
    setErrorMessage(msg);
    set_notification_variable(true, 'error', msg);
  };

  const handleSubmit = async () => {
    setErrorMessage('');

    if (targetStudentUUID === initialUUID) {
      fail('Self-transfer is not permitted');
      return;
    }

    if (!(transferCredits > 0)) {
      fail('Credits to transfer must be greater than 0');
      return;
    }

    if (transferCredits > maxCredits) {
      fail(
        `Cannot transfer ${transferCredits} credits; only ${maxCredits} remaining in this cycle`
      );
      return;
    }

    const apiUrl = await get_api_url('credits_transfer');

    const payload: CreditTransferPayload = {
      from_student_uuid: initialUUID,
      credits_to_transfer: transferCredits,
      to_student_uuid: targetStudentUUID,
      reason: reasonForTransfer
    };

    setSubmitting(true);
    try {
      const response = await post_api(apiUrl, payload, true);
      if (response.data.status === 'success') {
        set_notification_variable(true, 'success', 'Transfer Successful');
        loadStudentData();
        onClose();
      } else {
        fail(response?.data?.message || 'Transfer Unsuccessful');
      }
    } catch (error) {
      // the server states exactly why it refused — non-member, same family, above remaining
      fail(
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          (error as { message?: string })?.message ||
          'Transfer Unsuccessful'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto bg-white rounded-2xl shadow p-6 mt-[10px]" style={{ width: '550px' }}>
      <h2 className="text-xl font-semibold text-gray-800 flex gap-2 items-center">
        <CardIcon />

        <span className="inline-block text-black">Credit Transfer</span>
      </h2>

      <div className="flex items-center justify-center space-x-4 bg-gray-100 rounded-lg py-2 mt-6">
        <span className="flex items-center space-x-1 text-gray-700 font-medium">
          <span>👤</span>
          <span>From Student</span>
        </span>
        <span>➡️</span>
        <span className="flex items-center space-x-1 text-gray-700 font-medium">
          <span>👤</span>
          <span>To Student</span>
        </span>
      </div>

      <div>
        <label
          className="block text-sm text-gray-700 mb-1 mt-6 font-semibold"
          style={{ textTransform: 'unset' }}
        >
          From Student UUID <span className="text-[red]">*</span>{' '}
        </label>
        <textarea
          className="border h-[80px] border-gray-300 rounded-md w-full p-2 bg-gray-100 text-gray-400 cursor-not-allowed opacity-50"
          value={initialUUID}
          disabled
        />
      </div>

      <div>
        <label
          className="block text-sm text-gray-700 mb-1 mt-6 font-semibold"
          style={{ textTransform: 'unset' }}
        >
          Target Student UUID <span className="text-[red]">*</span>
        </label>
        <textarea
          className="h-[80px] border border-gray-300 rounded-md w-full p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={targetStudentUUID}
          onChange={(e) => setTargetStudentUUID(e.target.value.trim())}
          placeholder="Enter the UUID of the student receiving the credits..."
        />
      </div>

      <div>
        <label
          className="block text-sm text-gray-700 mb-1 mt-6 font-semibold"
          style={{ textTransform: 'unset' }}
        >
          Credit Transfer <span className="text-[red]">*</span>
          <span className="text-red-400">
            (the maximum credits that can be transfered are{' '}
            <span className="text-red-600">{maxCredits}</span>)
          </span>
        </label>
        <input
          min={1}
          max={maxCredits}
          type="number"
          className="w-full rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Enter credit amount..."
          value={transferCredits}
          onChange={(e) => setTransferCredits(parseFloat(e.target.value))}
        />
      </div>

      <div>
        <label
          className="block text-sm text-gray-700 mb-1 mt-6 font-semibold"
          style={{ textTransform: 'unset' }}
        >
          Reason for transfering of credits <span className="text-[red]">*</span>
        </label>
        <textarea
          className="h-[80px] border border-gray-300 rounded-md w-full p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={reasonForTransfer}
          onChange={(e) => setReasonForTransfer(e.target.value)}
          placeholder="Enter the reason for the transfer..."
        />
      </div>

      {!!errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-md mt-6">
          {errorMessage}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm p-3 rounded-md mt-6">
        <div className="flex items-center space-x-2">
          <CardIcon width={20} height={20} />
          <span className="text-[#1d4fd8] font-bold">Transfer Information</span>
        </div>
        <p className="mt-1">
          Both students must be members, and they cannot belong to the same family plan — a family
          organizer&apos;s hours already cover every member. Please ensure both student UUIDs are
          valid and the credit amount is accurate before submitting the transfer request.
        </p>
      </div>

      <div className="flex justify-center gap-3 py-5">
        <button
          onClick={handleSubmit}
          disabled={!targetStudentUUID || !transferCredits || !reasonForTransfer || submitting}
          className={`px-6 py-2 w-full rounded border text-white ${
            !targetStudentUUID || !transferCredits || !reasonForTransfer || submitting
              ? 'bg-blue-600 border-blue-700 opacity-60 cursor-not-allowed'
              : 'bg-blue-600 border-blue-700 hover:bg-blue-700 cursor-pointer'
          }`}
        >
          {submitting ? 'Transferring…' : 'Submit Transfer'}
        </button>
      </div>
    </div>
  );
};

export default TransferCredits;
